import { createExpiringStorageEntry, isNonEmptyString } from "./expiringStorageEntry";

// DEV-2216: storage for Amazon's Login URI params. A plain module (no React,
// router or AuthContext) so the sales source registry can import the clear
// helper without pulling the hooks in. AuthGate's capture writes the entry,
// useAmazonPendingAuthorize reads it, and the prompt's Connect / Not now
// clears it.
export const AMAZON_PENDING_AUTHORIZE_STORAGE_KEY = "cpa_ui_amazon_pending_authorize";

// Amazon documents the whole workflow as breaking after 10 minutes, so an
// older entry can only fail at Seller Central.
const PENDING_AUTHORIZE_TTL_MS = 10 * 60 * 1000;

// One normalization for both the write and the read: the three required
// params must be non-empty strings, and an empty version is stored as null.
function normalizeLogin({ amazonCallbackUri, amazonState, sellingPartnerId, version }) {
  if (!isNonEmptyString(amazonCallbackUri) || !isNonEmptyString(amazonState) || !isNonEmptyString(sellingPartnerId)) {
    return null;
  }
  return { amazonCallbackUri, amazonState, sellingPartnerId, version: isNonEmptyString(version) ? version : null };
}

const pendingAuthorizeEntry = createExpiringStorageEntry(
  AMAZON_PENDING_AUTHORIZE_STORAGE_KEY,
  PENDING_AUTHORIZE_TTL_MS,
  normalizeLogin,
);

/**
 * Persists the Login URI params. Returns whether the write landed (it is read
 * back, as some browsers silently drop writes), so the capture can report
 * blocked storage instead of losing the request without a word.
 */
export function persistAmazonPendingAuthorize(login) {
  return pendingAuthorizeEntry.persist(login);
}

/** The persisted entry, or null when missing, malformed or older than 10 minutes. */
export function readAmazonPendingAuthorize() {
  return pendingAuthorizeEntry.read();
}

/** Clears the entry: once the prompt is acted on (Connect or Not now), or found expired. */
export function clearAmazonPendingAuthorize() {
  pendingAuthorizeEntry.clear();
}

// The Amazon connect flow is off unless REACT_APP_AMAZON_CONNECT_ENABLED is
// exactly "true": amazon-oauth is not deployed until Amazon approves the
// developer registration (DEV-2207). CRA inlines REACT_APP_* at build time, so
// in a bundle this is a constant (changing it needs a redeploy); tests set it
// per test.
export function amazonConnectEnabled() {
  return process.env.REACT_APP_AMAZON_CONNECT_ENABLED === "true";
}

// Amazon's Login URI params (DEV-2216). `version` is only stripped together
// with the other three, never on its own. A stable module-level array, as
// AuthGate's useStripParams requires.
export const AMAZON_LOGIN_PARAMS = ["amazon_callback_uri", "amazon_state", "selling_partner_id", "version"];
